import api from './client';

export type OrderStatus = 'Занесение' | 'В работе' | 'Выполнено';

export const ORDER_STATUSES: OrderStatus[] = [
  'Занесение', 'В работе', 'Выполнено',
];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  'Занесение': 'Занесение',
  'В работе': 'В работе',
  'Выполнено': 'Выполнено',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  'Занесение': 'default',
  'В работе': 'processing',
  'Выполнено': 'success',
};

export type ClientType = 'individual' | 'counterparty';

export interface OrderItem {
  item_type: 'service' | 'part';
  name: string;
  external_1c_id?: string | null;
  quantity: number;
  unit: string;
  price: number;
  recommended_price?: number | null;
  // Цена из клиентского прайса — в виде «Замена масла» цена масла read-only
  // (OilChangePage.md rev. 2026-09-12 №19)
  price_from_price_list?: boolean | null;
  sum?: number | null;
  product_id?: string | null;
  product_name?: string | null;
  service_name?: string | null;
  service_catalog_id?: string | null;
  // Исполнители
  executor1?: string | null;
  executor1_id?: string | null;
  executor2?: string | null;
  executor2_id?: string | null;
  executor3?: string | null;
  executor3_id?: string | null;
  // НДС
  vat?: number | null;
  vat_rate?: string | null;
  sum_with_vat?: number | null;
  // Для услуг
  norm_hour?: number | null;
  work_code?: string | null;
  // Маркировка
  marking_code?: string | null;
  // У марки в реестре есть криптохвост (rev. 2026-09-17): false → марка неполная, красный текст
  mark_has_full_code?: boolean | null;
  // Отложенная позиция режима «без оприходования» (unreceived_sales.md v3)
  unreceived_pending?: boolean;
  // Вычисляемый статус марки в чеке ККМ (реплика иконки 1С): ok | mismatch | missing
  mark_status?: 'ok' | 'mismatch' | 'missing' | null;
}

export interface OrderItemIn {
  item_type: 'service' | 'part';
  name: string;
  external_1c_id?: string | null;
  quantity?: number;
  unit?: string;
  price: number;
  sum?: number | null;
  // Исполнители (для услуг)
  executor1_id?: string | null;
  executor2_id?: string | null;
  executor3_id?: string | null;
  // НДС
  vat?: number | null;
  vat_rate?: string | null;
  sum_with_vat?: number | null;
  // Для услуг
  norm_hour?: number | null;
  work_code?: string | null;
  service_catalog_id?: string | null;
  // Маркировка
  marking_code?: string | null;
}

export interface RelatedDoc {
  line_number: number;
  sales_doc: string;
  posted: boolean;
  sales_doc_id?: string | null;
  sales_doc_kind?: 'cash_receipt' | 'sale' | 'sales_report' | string | null;
}

export interface ServiceOrder {
  id: number;
  number: string | null;
  status: OrderStatus;
  posted: boolean;
  // Вид заказа (oil_change_queue.md): service_order | oil_change | quick_sale (QuickSalePage.md)
  order_type?: 'service_order' | 'oil_change' | 'quick_sale';
  // Состояние в очереди на пост замены масла (null/undefined — не в очереди)
  queue_status?: 'queued' | null;
  // Оптимистичная блокировка (1c.md §3 rev. 19): версия контента заказа
  content_version?: number;
  // Двухстадийное удаление (service_order_deletion.md):
  // null/undefined — обычный; pending — помечен (read-only); done — скрыт
  deletion_state?: 'pending' | 'done' | null;
  deletion_requested_at?: string | null;
  deletion_requested_by?: string | null;
  created_at: string;
  external_1c_id?: string | null;
  external_1c_number?: string | null;
  external_1c_cash_receipt_id?: string | null;
  external_1c_cash_receipt_number?: string | null;
  // Юрлицо (rev. 7): ЗН АвтоРегиум + Реализация ТоваровУслуг
  external_1c_parts_order_id?: string | null;
  external_1c_parts_order_number?: string | null;
  external_1c_sale_id?: string | null;
  external_1c_sale_number?: string | null;
  // Реализация проведена (null/undefined — реализации нет)
  sale_posted?: boolean | null;
  external_1c_service_sale_id?: string | null;
  external_1c_service_sale_number?: string | null;
  service_sale_posted?: boolean | null;
  external_1c_invoice_id?: string | null;
  external_1c_invoice_number?: string | null;
  invoice_posted?: boolean | null;
  external_1c_service_invoice_id?: string | null;
  external_1c_service_invoice_number?: string | null;
  service_invoice_posted?: boolean | null;
  // Чек ККМ пробит (null/undefined — чека нет) — «Пробить чек» (direct_posting rev. №8)
  receipt_posted?: boolean | null;
  // Чек ККМ услуг (Фомин, direct_posting rev. №11) — «Пробить чек услуг»
  external_1c_service_receipt_id?: string | null;
  service_receipt_posted?: boolean | null;
  // Есть отложенные позиции режима «без оприходования» — тег «не в обороте» в списке
  has_unreceived_pending?: boolean;
  // Контрагент / Организация / Менеджер
  counterparty?: string | null;
  counterparty_id?: string | null;
  retail_buyer_name?: string | null;
  // Локальная привязка клиента (матч по counterparty_id из 1С)
  client_type?: ClientType | null;
  client_id?: number | null;
  organization?: string | null;
  organization_id?: string | null;
  org_bank_account_id?: string | null;
  manager?: string | null;
  // Автомобиль
  vehicle?: string | null;
  vehicle_id?: string | null;
  vin?: string | null;
  license_plate?: string | null;
  manufacture_year?: number | null;
  color?: string | null;
  engine_type?: string | null;
  engine_number?: string | null;
  body_number?: string | null;
  mileage?: number | null;
  purchase_date?: string | null;
  repair_cost?: number | null;
  special_notes?: string | null;
  // Работы
  work_start_date?: string | null;
  work_end_date?: string | null;
  norm_hour?: number | null;
  regulatory_doc?: string | null;
  recommendations?: string | null;
  // Оплата
  payment_type?: string | null;
  card_payment?: boolean;
  bank_terminal?: string | null;
  prepayment_sum?: number | null;
  print_receipt?: boolean;
  // НДС
  vat_for_products?: boolean;
  vat_for_services?: boolean;
  // Страховка
  insurer?: string | null;
  // Итоги
  total_services: number;
  total_parts: number;
  total: number;
  items: OrderItem[];
  // Предупреждение при проблемах с 1С
  warning?: string | null;
  // Связанные документы
  related_docs?: RelatedDoc[];
}

export interface ServiceOrderListResponse {
  items: ServiceOrder[];
  total: number;
}

export interface OrderCreatePayload {
  vehicle_id?: string | null;
  // Автомобиль из справочника AR_DB (vehicle_resolver.md) — приоритет над vehicle_id
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  manufacture_year?: number | null;
  color?: string | null;
  client_type?: ClientType | null;
  client_id?: number | null;
  retail_buyer_name?: string | null;
  // Вид заказа: при oil_change заказ автоматически встаёт в очередь на пост
  order_type?: 'service_order' | 'oil_change' | 'quick_sale';
  // false — не ставить новую замену масла в очередь (oil_change_queue.md §3 п.1, rev. 2026-09-11 №3)
  enqueue?: boolean;
  mileage?: number | null;
  recommendations?: string | null;
  prepayment_sum?: number | null;
  work_start_date?: string | null;
  work_end_date?: string | null;
  items?: OrderItemIn[];
  payment_type?: 'Касса' | 'Банк' | null;
  card_payment?: boolean;
  print_receipt?: boolean;
  vat_for_products?: boolean;
  vat_for_services?: boolean;
  bank_terminal?: string | null;
  org_bank_account_id?: string | null;
  // Экстренный обход барьера «кассовая смена закрыта» (1c.md §1 rev. 21)
  allow_closed_shift?: boolean;
}

export interface OrderUpdatePayload {
  vehicle_id?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  manufacture_year?: number | null;
  color?: string | null;
  client_type?: ClientType | null;
  client_id?: number | null;
  retail_buyer_name?: string | null;
  mileage?: number | null;
  recommendations?: string | null;
  prepayment_sum?: number | null;
  work_start_date?: string | null;
  work_end_date?: string | null;
  items?: OrderItemIn[];
  payment_type?: 'Касса' | 'Банк' | null;
  card_payment?: boolean;
  print_receipt?: boolean;
  vat_for_products?: boolean;
  vat_for_services?: boolean;
  bank_terminal?: string | null;
  org_bank_account_id?: string | null;
  // Оптимистичная блокировка (1c.md §3 rev. 19): версия из загруженного заказа
  base_version?: number;
  // Экстренный обход барьера «кассовая смена закрыта» (1c.md §1 rev. 21)
  allow_closed_shift?: boolean;
}

// Объединённый поиск клиентов (физлица + юрлица)
export interface ClientSearchItem {
  type: ClientType;
  id: number;
  label: string;
  sublabel?: string | null;
}
export const searchClients = (q?: string): Promise<ClientSearchItem[]> =>
  api.get<{ items: ClientSearchItem[] }>('/clients/search', { params: { q, limit: 30 } }).then(r => r.data.items);

export const listOrders = (params: {
  page?: number; per_page?: number; search?: string;
  status?: OrderStatus; legal_entity?: 'autoregium' | 'fomin';
} = {}): Promise<ServiceOrderListResponse> =>
  api.get<ServiceOrderListResponse>('/service-orders', { params }).then(r => r.data);

export const getOrder = (id: number): Promise<ServiceOrder> =>
  api.get<ServiceOrder>(`/service-orders/${id}`).then(r => r.data);

export const createOrder = (data: OrderCreatePayload): Promise<ServiceOrder> =>
  api.post<ServiceOrder>('/service-orders', data).then(r => r.data);

export const updateOrder = (id: number, data: OrderUpdatePayload): Promise<ServiceOrder> =>
  api.put<ServiceOrder>(`/service-orders/${id}`, data).then(r => r.data);

export const deleteOrder = (id: number): Promise<void> =>
  api.delete(`/service-orders/${id}`).then(() => undefined);

// Двухстадийное удаление (service_order_deletion.md)
export interface DeletionDoc {
  kind: string;
  kind_name: string;
  number?: string | null;
}
export interface DeletionResponse {
  deletion_state: string;
  onec_documents: DeletionDoc[];
}

export const requestOrderDeletion = (id: number): Promise<DeletionResponse> =>
  api.post<DeletionResponse>(`/service-orders/${id}/request-deletion`).then(r => r.data);

export const cancelOrderDeletion = (id: number): Promise<DeletionResponse> =>
  api.post<DeletionResponse>(`/service-orders/${id}/cancel-deletion`).then(r => r.data);

// Переключение вида заказа (тумблер «Показать в виде замены масла»).
// Локальная правка, в 1С не ходит (oil_change_queue.md §4).
export const setOrderType = (id: number, orderType: 'service_order' | 'oil_change' | 'quick_sale'): Promise<void> =>
  api.patch(`/service-orders/${id}/order-type`, { order_type: orderType }).then(() => undefined);
